import { Deal, HasCoapplicant } from '@upstart/harmony/lib/legos/dealTypes.js';
import {
  CustomerSchema,
  LeadSchema,
  RequiredFieldConfig,
  defaultRequiredFieldsConfig,
} from '../CustomerFormProvider.tsx';
import { CrmType, SourceType } from '@upstart/harmony/lib/constants.js';
import { formatPhone } from '~/utils/formatters/index.ts';
import { CustomerFormSourceMapping, Map } from '../Sections/LeadSection.tsx';
import { CustomerFormSections } from '../CustomerForm.tsx';

export type CustomerFormDeal = Pick<
  Deal,
  'source' | 'applicant' | 'coapplicant' | 'crmMeta'
>;

export const dealToCustomerSchema = (deal: Deal): CustomerSchema => {
  return {
    isInShowroom: deal.crmMeta?.inShowroom ?? false,
    addCoapplicantToggle: deal.applicant?.hasCoapplicant === HasCoapplicant.yes,
    contact: {
      firstName: deal.applicant?.firstName ?? '',
      middleName: deal.applicant?.middleName ?? '',
      lastName: deal.applicant?.lastName ?? '',
      email: deal.applicant?.email ?? '',
      homePhone: formatPhone(deal.applicant?.homePhone ?? ''),
      mobilePhone: formatPhone(deal.applicant?.phone ?? ''),
    },
    lead: {
      type: deal.source ?? '',
      sourceId: String(deal.crmMeta?.sourceId ?? ''),
      sourceName: deal.crmMeta?.sourceName ?? '',
      secondaryId: String(deal.crmMeta?.secondarySourceId ?? ''),
      secondarySourceName: deal.crmMeta?.secondarySourceName ?? '',
      trackingCode: String(deal.crmMeta?.trackingCodeId ?? ''),
      trackingCodeName: deal.crmMeta?.trackingCodeName ?? '',
    },
    address: {
      streetAddress: deal.applicant?.address ?? '',
      apt: deal.applicant?.apt ?? '',
      city: deal.applicant?.city ?? '',
      state: deal.applicant?.state ?? '',
      zip: deal.applicant?.zip ?? '',
      county: deal.applicant?.county ?? '',
    },
    coapplicant: {
      firstName: deal.coapplicant?.firstName ?? '',
      middleName: deal.coapplicant?.middleName ?? '',
      lastName: deal.coapplicant?.lastName ?? '',
      email: deal.coapplicant?.email ?? '',
      homePhone: formatPhone(deal.coapplicant?.homePhone ?? ''),
      mobilePhone: formatPhone(deal.coapplicant?.phone ?? ''),
    },
    coapplicantAddress: {
      streetAddress: deal.coapplicant?.address ?? '',
      apt: deal.coapplicant?.apt ?? '',
      city: deal.coapplicant?.city ?? '',
      state: deal.coapplicant?.state ?? '',
      zip: deal.coapplicant?.zip ?? '',
      county: deal.coapplicant?.county ?? '',
    },
  };
};

type RequiredConfig = { required?: boolean };
export type CustomerInfoConfig =
  | {
      email?: RequiredConfig;
      phone?: RequiredConfig;
      zipCode?: RequiredConfig;
      trackingCode?: RequiredConfig;
    }
  | undefined;

export const getRequiredFields = (
  sourceMapping?: CustomerFormSourceMapping,
  customerInfoConfig?: CustomerInfoConfig,
  crmType?: CrmType,
): RequiredFieldConfig => {
  const sourceIsRequired =
    crmType !== CrmType.STANDALONE && sourceMapping?.mapping;
  const trackingIsRequired =
    crmType === CrmType.DEALERSOCKET &&
    sourceMapping?.trackingCode?.length &&
    customerInfoConfig?.trackingCode?.required;

  return {
    ...defaultRequiredFieldsConfig,
    email:
      !customerInfoConfig?.phone?.required ||
      !!customerInfoConfig?.email?.required,
    phone: !!customerInfoConfig?.phone?.required,
    zip: !!customerInfoConfig?.zipCode?.required,
    source: !!sourceIsRequired,
    trackingCode: !!trackingIsRequired,
  };
};

const cleanEmailPropInDeal = (deal: CustomerFormDeal): CustomerFormDeal => {
  if (!deal.applicant?.email) {
    // deepcode ignore DeleteOfNonProperty: Intentional prop deletion
    delete deal.applicant?.email;
  }

  if (!deal.coapplicant?.email) {
    // deepcode ignore DeleteOfNonProperty: Intentional prop deletion
    delete deal.coapplicant?.email;
  }
  return deal;
};

export const customerSchemaToAddApplicant = (
  customer: CustomerSchema,
): CustomerFormDeal => {
  return cleanEmailPropInDeal({
    source: customer.lead?.type as SourceType,
    crmMeta: {
      inShowroom: customer.isInShowroom,
      sourceId: customer.lead?.sourceId ?? undefined,
      sourceName: customer.lead?.sourceName,
      secondarySourceId: customer.lead?.secondaryId ?? undefined,
      secondarySourceName: customer.lead?.secondarySourceName,
      trackingCodeId: customer.lead?.trackingCode ?? undefined,
      trackingCodeName: customer.lead?.trackingCodeName,
    },
    applicant: {
      firstName: customer.contact.firstName,
      middleName: customer.contact.middleName,
      lastName: customer.contact.lastName,
      email: customer.contact.email,
      homePhone: customer.contact.homePhone,
      phone: customer.contact.mobilePhone,
      address: customer.address.streetAddress,
      apt: customer.address.apt,
      city: customer.address.city,
      county: customer.address.county,
      state: customer.address.state,
      zip: customer.address.zip,
      hasCoapplicant: customer.addCoapplicantToggle
        ? HasCoapplicant.yes
        : HasCoapplicant.no,
    },
    coapplicant:
      customer.addCoapplicantToggle === true
        ? {
            firstName: customer.coapplicant.firstName,
            middleName: customer.coapplicant.middleName,
            lastName: customer.coapplicant.lastName,
            email: customer.coapplicant.email,
            homePhone: customer.coapplicant.homePhone,
            phone: customer.coapplicant.mobilePhone,
            address: customer.coapplicantAddress.streetAddress,
            apt: customer.coapplicantAddress.apt,
            city: customer.coapplicantAddress.city,
            county: customer.coapplicantAddress.county,
            state: customer.coapplicantAddress.state,
            zip: customer.coapplicantAddress.zip,
          }
        : {},
  });
};

export const customerSchemaToUpdateApplicant = (
  customer: CustomerSchema,
  sectionKey: CustomerFormSections,
): CustomerFormDeal | undefined => {
  switch (sectionKey) {
    case CustomerFormSections.isInShowroom:
      return {
        crmMeta: { inShowroom: customer.isInShowroom },
      };
    case CustomerFormSections.addCoapplicantToggle:
      return {
        applicant: {
          hasCoapplicant:
            customer.addCoapplicantToggle === true
              ? HasCoapplicant.yes
              : HasCoapplicant.no,
        },
      };
    case CustomerFormSections.contact:
      return cleanEmailPropInDeal({
        applicant: {
          firstName: customer.contact.firstName,
          middleName: customer.contact.middleName,
          lastName: customer.contact.lastName,
          email: customer.contact.email,
          phone: customer.contact.mobilePhone,
          homePhone: customer.contact.homePhone,
          hasCoapplicant:
            customer.addCoapplicantToggle === true
              ? HasCoapplicant.yes
              : HasCoapplicant.no,
        },
      });
    case CustomerFormSections.address:
      return {
        applicant: {
          address: customer.address.streetAddress,
          apt: customer.address.apt,
          city: customer.address.city,
          state: customer.address.state,
          zip: customer.address.zip,
          county: customer.address.county,
          hasCoapplicant:
            customer.addCoapplicantToggle === true
              ? HasCoapplicant.yes
              : HasCoapplicant.no,
        },
      };
    case CustomerFormSections.coapplicant:
      return cleanEmailPropInDeal({
        coapplicant: {
          firstName: customer.coapplicant.firstName,
          middleName: customer.coapplicant.middleName,
          lastName: customer.coapplicant.lastName,
          email: customer.coapplicant.email,
          phone: customer.coapplicant.mobilePhone,
          homePhone: customer.coapplicant.homePhone,
        },
      });
    case CustomerFormSections.coapplicantAddress:
      return {
        coapplicant: {
          address: customer.coapplicantAddress.streetAddress,
          apt: customer.coapplicantAddress.apt,
          city: customer.coapplicantAddress.city,
          state: customer.coapplicantAddress.state,
          zip: customer.coapplicantAddress.zip,
          county: customer.coapplicantAddress.county,
        },
      };
    default:
      return;
  }
};

export const getCreditRange = (deal: Deal): string | undefined => {
  if (deal && deal.creditPull && deal.creditPull.creditTier) {
    const creditTier = deal.creditPull.creditTier;
    const low = creditTier.low || 'NA';
    const high = creditTier.high || 'NA';
    return `${low}-${high}`;
  } else if (deal?.creditRange) {
    // if no credit pull, use customer selected credit range
    const low = deal.creditRange.low || 'NA';
    const high = deal.creditRange.high || 'NA';
    return `${low}-${high}`;
  }
};

export const getDealMapping = (
  sourceMapping: CustomerFormSourceMapping,
  leadInfo?: LeadSchema,
): {
  sourceMap: Map | undefined;
  secondaryMap: Map | undefined;
  trackingMap: Map | undefined;
} => {
  const typeMapping = sourceMapping.mapping ?? {};
  const sourceTypeMapping = typeMapping[leadInfo?.type ?? ''] ?? [];
  const sourceMap = sourceTypeMapping.find(
    (map) => map.id == leadInfo?.sourceId || map.name === leadInfo?.sourceName,
  );
  const secondaryMap = sourceMapping?.secondary?.find(
    (map) =>
      map.id == leadInfo?.secondaryId ||
      map.name == leadInfo?.secondarySourceName,
  );
  const trackingMap = sourceMapping?.trackingCode?.find(
    (map) =>
      map.id == leadInfo?.trackingCode ||
      map.name === leadInfo?.trackingCodeName,
  );

  return {
    sourceMap,
    secondaryMap,
    trackingMap,
  };
};
